// ─── Casing Reference Data ──────────────────────────────────────────────────
// Shared across NewJobScreen, PipeTorqueFields, and JobDetail.

export const CASING_SIZES = [
  { inches: '4.500', mm: '114.30', label: '4-1/2"' },
  { inches: '5.000', mm: '127.00', label: '5"' },
  { inches: '5.500', mm: '139.70', label: '5-1/2"' },
  { inches: '6.625', mm: '168.28', label: '6-5/8"' },
  { inches: '7.000', mm: '177.80', label: '7"' },
  { inches: '7.625', mm: '193.68', label: '7-5/8"' },
  { inches: '8.625', mm: '219.08', label: '8-5/8"' },
  { inches: '9.625', mm: '244.48', label: '9-5/8"' },
  { inches: '10.750', mm: '273.05', label: '10-3/4"' },
  { inches: '11.750', mm: '298.45', label: '11-3/4"' },
  { inches: '13.375', mm: '339.73', label: '13-3/8"' },
  { inches: '16.000', mm: '406.40', label: '16"' },
  { inches: '18.625', mm: '473.08', label: '18-5/8"' },
  { inches: '20.000', mm: '508.00', label: '20"' },
];

/**
 * Convert an inches value to the canonical torque-profile size key.
 *
 * For catalog sizes this returns the fractional label (e.g. '4-1/2"').
 * For genuine custom sizes this returns '<n>"' (e.g. '4.875"').
 * Returns '' for null / blank / the sentinel string "custom".
 */
export function canonicalTorqueSize(inches: string | number | null | undefined): string {
  if (inches == null || inches === '' || inches === 'custom') return '';
  const exact = CASING_SIZES.find(s => s.inches === String(inches));
  if (exact) return exact.label;
  const n = Number(inches);
  if (!Number.isFinite(n)) return '';
  // Dedupe: a custom-typed value that matches a catalog size numerically
  const byNum = CASING_SIZES.find(s => Number(s.inches) === n);
  if (byNum) return byNum.label;
  return `${n}"`;
}

// ─── Tubing Reference Data (API RP 5C1 Table 3) ─────────────────────────────
// Mirrors the backend TUBING_SIZES (src/services/casing-data.ts). Only the sizes
// the backend's canonicalTubingSize round-trips are listed here so the job form,
// the detail-page torque preview, and the server-side PDF all agree on the size
// key fed into lookupTubingSpec.

export const TUBING_SIZES = [
  { inches: '1.050', mm: '26.67', label: '1.050"' },
  { inches: '1.315', mm: '33.40', label: '1.315"' },
  { inches: '1.660', mm: '42.16', label: '1.660"' },
  { inches: '1.900', mm: '48.26', label: '1.900"' },
  { inches: '2.063', mm: '52.40', label: '2.063"' },
  { inches: '2.375', mm: '60.32', label: '2-3/8"' },
  { inches: '2.875', mm: '73.02', label: '2-7/8"' },
  { inches: '3.500', mm: '88.90', label: '3-1/2"' },
  { inches: '4.000', mm: '101.60', label: '4"' },
  { inches: '4.500', mm: '114.30', label: '4-1/2"' },
];

/** Tubing grades per API RP 5C1 Table 3 (distinct from CasingGrade). */
export const TUBING_GRADES = ['H-40', 'J-55', 'C-75', 'L-80', 'N-80', 'C-90', 'P-105'];

// ─── Pipe-grade canonicalization ────────────────────────────────────────────
//
// MIRRORS fieldiq-api/src/constants/casing-grades.ts. Two repos, no shared
// package, so the rule is copied rather than imported.
//
// THE COPY IS NOT LOAD-BEARING, and that is deliberate. The API canonicalizes
// the grade on every write (POST /torque-profiles, POST/PUT /jobs) and on the
// GET /torque-profiles query param, so it is the authority on what gets stored
// and what a lookup matches. If this copy drifts, the worst case is that the
// UI sends a variant spelling and the server resolves it correctly anyway —
// not a second row with different torque. That WAS the failure mode: this
// function used to be `grade.replace(/-/g, '')`, the only normalization
// anywhere, and a variant spelling missed the tenant's existing profile,
// showed an empty torque panel, and got a made-up number saved next to it.
//
// The fold is case, whitespace and ASCII hyphens ONLY. 'TN 110S' and
// 'TN 110SS' are SEPARATE grades in the Tenaris OCTG catalog — the trailing SS
// is sour service — and must never fold together.

/** Uppercase, remove all whitespace, remove all ASCII hyphens. */
export function foldGradeKey(grade: string): string {
  return grade.toUpperCase().replace(/[\s-]+/g, '');
}

/** Canonical display spellings, keyed by folding key. */
export const GRADE_CANONICAL: Record<string, string> = {
  H40: 'H-40',
  J55: 'J-55',
  K55: 'K-55',
  SOO55: 'SOO-55',
  M65: 'M-65',
  N80: 'N-80',
  L80: 'L-80',
  '13CRL80': '13Cr L-80',
  PS80: 'PS-80',
  TN80SS: 'TN-80SS',
  C90: 'C-90',
  T95: 'T-95',
  P110: 'P-110',
  Q125: 'Q-125',
  C75: 'C-75',
  P105: 'P-105',
  L80ICY: 'L-80 ICY',
  P110ICY: 'P-110 ICY',
  VASS95XP: 'VA-SS-95-XP',
  TN110S: 'TN-110S',
  TN110SS: 'TN-110SS',
};

const GRADE_SHAPE = /^([0-9]*[A-Z]+)([0-9]+)([A-Z0-9]*)$/;

/**
 * Canonicalize a pipe grade to its stored/display form. Pure function of the
 * folding key — two inputs that fold together MUST produce the same output.
 * An unrecognized grade is canonicalized and kept, never rejected.
 */
export function canonicalizeGrade(grade: string | null | undefined): string | null {
  if (grade == null) return null;
  const key = foldGradeKey(grade);
  if (key === '') return null;

  const known = GRADE_CANONICAL[key];
  if (known) return known;

  const m = GRADE_SHAPE.exec(key);
  if (m) return `${m[1]}-${m[2]}${m[3]}`;

  return key;
}

/** Base tubing connections. Tenant-custom connections are merged in alongside these. */
export const TUBING_THREADS = ['NU', 'EUE', 'IJ'];

const TUBING_THREAD_SET = new Set(TUBING_THREADS);

/**
 * True when a stored thread value identifies a tubing connection (NU/EUE/IJ).
 * Matches the backend isTubingThread exactly — the single signal that routes a
 * string to the tubing torque table vs. the casing tables. Custom connection
 * names return false (they resolve via the tenant torque-profiles table, same
 * as custom casing connections).
 */
export function isTubingThread(thread: string | null | undefined): boolean {
  return thread != null && TUBING_THREAD_SET.has(String(thread).trim().toUpperCase());
}

/** Convert decimal inches to the canonical size key used in the tubing spec table. */
export function canonicalTubingSize(inches: string | number | null | undefined): string {
  if (inches == null || inches === '' || inches === 'custom') return '';
  const n = Number(inches);
  if (!Number.isFinite(n)) return '';
  const match = TUBING_SIZES.find(s => Math.abs(Number(s.inches) - n) < 0.005);
  return match ? match.label : `${n}"`;
}
